import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { creerClient, ErreurApi, type Client } from '../../api/client.js';
import type { Ecran } from '../../api/contrat.js';
import { modeMocks } from '../../app/entree.js';
import { COULEURS, POLICE_SYSTEME } from '../../app/styles.js';
import { PageArbre, PageDispositions, PageMonArbre } from './pages.js';
import { adapterArbreEquipe, adapterBranche } from './source.js';
import type { DonneesArbre, PersonneFamille, StatutContact } from './types.js';

/**
 * Conteneurs des écrans de la généalogie dans le registre de la coquille (`{ ecran }`) : ils lisent l'API. Les données
 * d'exemple n'existent qu'en développement avec VITE_LAKOU_MOCKS=1 (`modeMocks`) ; un build de production n'y retombe jamais.
 */
type Lecture<T> = { statut: 'chargement' } | { statut: 'ok'; donnees: T } | { statut: 'erreur'; message: string };

const MESSAGE_INCONNU = 'Une erreur est survenue. Réessayez dans un instant ; si elle persiste, contactez le Cabinet MAAT.';
const messageDe = (e: unknown): string => (e instanceof ErreurApi ? e.messageUtilisateur : MESSAGE_INCONNU);

function useLecture<T>(charger: () => Promise<T>): Lecture<T> & { relire: () => void } {
  const [lecture, setLecture] = useState<Lecture<T>>({ statut: 'chargement' });
  // ARB-GENEA-09 : relire après une écriture (ajout d'une personne). Pendant la relecture, les données affichées restent en place :
  // l'écran ne repasse pas par « Chargement », qui effacerait le message de l'enregistrement.
  const [tour, setTour] = useState(0);
  useEffect(() => {
    let annule = false;
    charger().then(
      (donnees) => !annule && setLecture({ statut: 'ok', donnees }),
      (e: unknown) => !annule && setLecture({ statut: 'erreur', message: messageDe(e) }),
    );
    return () => {
      annule = true;
    };
    // Lu au montage de l'écran, puis à chaque relecture demandée.
  }, [tour]);
  return { ...lecture, relire: () => setTour((t) => t + 1) };
}

const clientDeLEcran = () => creerClient({ fetch: (...a) => globalThis.fetch(...a) });

/** Titre du contrat sans la précision interne « (portail client) ». */
export const titreClient = (titre: string): string => titre.replace(/\s*\(portail client\)\s*/i, ' ').trim();

export function EtatEcran({ ecran, titre, enfants }: { ecran: Ecran; titre?: string; enfants: ReactNode }) {
  return (
    <section data-ecran={ecran.id} style={{ fontFamily: POLICE_SYSTEME, fontSize: 18 }}>
      <h1 style={{ color: COULEURS.marine, fontSize: 28 }}>{titre ?? ecran.titre}</h1>
      {enfants}
    </section>
  );
}

function MonArbreReel({ ecran }: { ecran: Ecran }) {
  const client = useMemo(clientDeLEcran, []);
  const lecture = useLecture(() => client.appeler<PersonneFamille[]>('portail.branche'));
  const titre = titreClient(ecran.titre);
  if (lecture.statut === 'chargement')
    return <EtatEcran ecran={ecran} titre={titre} enfants={<p role="status">Chargement de votre arbre…</p>} />;
  if (lecture.statut === 'erreur') {
    return (
      <EtatEcran
        ecran={ecran}
        titre={titre}
        enfants={
          <p role="alert" style={{ color: COULEURS.rouge }}>
            {lecture.message}
          </p>
        }
      />
    );
  }
  const donnees: DonneesArbre = adapterBranche(lecture.donnees);
  // ARB-GENEA-46/47 : la ligne de la personne qui consulte et les seuls statuts « accepte », tels que l'API les porte.
  const viewerId = lecture.donnees.find((p) => p.isViewer)?.id ?? '';
  const statutsPortail: Record<string, StatutContact> = Object.fromEntries(
    lecture.donnees.filter((p) => p.contactStatus === 'accepte').map((p) => [p.id, 'accepte' as const]),
  );
  return <PageMonArbre ecran={{ id: ecran.id, titre }} donnees={donnees} viewerId={viewerId} statutsPortail={statutsPortail} />;
}

/** X15 « Mon arbre » : la branche réelle de l'héritier (portail.branche). */
export function EcranX15({ ecran }: { ecran: Ecran }) {
  if (modeMocks(import.meta.env)) return <PageMonArbre ecran={{ id: ecran.id, titre: titreClient(ecran.titre) }} exemple />;
  return <MonArbreReel ecran={ecran} />;
}

const dossierDeLAdresse = (): string | null => new URLSearchParams(globalThis.location?.search ?? '').get('dossier')?.trim() || null;

type RendreArbre = (props: {
  ecran: { id: string; titre: string };
  donnees?: DonneesArbre;
  exemple?: boolean;
  client?: Client;
  dossierId?: string;
  /** Après une écriture réussie (ajout d'une personne), l'arbre du dossier est relu (ARB-GENEA-09). */
  onModifie?: () => void;
}) => ReactNode;

function ArbreDuDossier({ ecran, dossierId, rendre }: { ecran: Ecran; dossierId: string; rendre: RendreArbre }) {
  const client = useMemo(clientDeLEcran, []);
  const lecture = useLecture(() =>
    client.appeler<Parameters<typeof adapterArbreEquipe>[0]>('genealogie.arbre', { parametres: { dossierId } }),
  );
  if (lecture.statut === 'chargement') return <EtatEcran ecran={ecran} enfants={<p role="status">Chargement de l’arbre du dossier…</p>} />;
  if (lecture.statut === 'erreur') {
    return (
      <EtatEcran
        ecran={ecran}
        enfants={
          <p role="alert" style={{ color: COULEURS.rouge }}>
            {lecture.message}
          </p>
        }
      />
    );
  }
  return <>{rendre({ ecran, donnees: adapterArbreEquipe(lecture.donnees), client, dossierId, onModifie: lecture.relire })}</>;
}

function EcranArbreEquipe({ ecran, rendre }: { ecran: Ecran; rendre: RendreArbre }) {
  if (modeMocks(import.meta.env)) return <>{rendre({ ecran, exemple: true })}</>;
  const dossierId = dossierDeLAdresse();
  if (dossierId === null) {
    return (
      <EtatEcran
        ecran={ecran}
        enfants={<p role="status">Aucun dossier n’est ouvert. Ouvrez un dossier depuis le pilotage pour voir son arbre.</p>}
      />
    );
  }
  return <ArbreDuDossier ecran={ecran} dossierId={dossierId} rendre={rendre} />;
}

/** D04 : l'arbre et les tableaux du dossier ouvert (?dossier=), lus par genealogie.arbre. */
export const EcranD04 = ({ ecran }: { ecran: Ecran }) => <EcranArbreEquipe ecran={ecran} rendre={(p) => <PageArbre {...p} />} />;

/** D04b : le même arbre réel en cinq dispositions. */
export const EcranD04b = ({ ecran }: { ecran: Ecran }) => <EcranArbreEquipe ecran={ecran} rendre={(p) => <PageDispositions {...p} />} />;
